import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel,
  Grid, Paper, Alert,
} from '@mui/material';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { WizardStep } from '@/components/wizard/WizardStep';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { useDataProvider } from 'react-admin';
import { buildTransitionChain } from '@/components/workflow/machineHelpers';
import { useRuleEvaluation } from '@/components/workflow/useRuleEvaluation';

const STEPS = [
  { id: 'details', title: 'Student Details' },
  { id: 'client-programme', title: 'Client & Programme' },
  { id: 'review', title: 'Review & Submit' },
];

interface FormValues {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  idNumber: string;
  gradeLevel: string;
  clientId: string;
  programmeId: string;
  enrollmentDate: string;
  notes: string;
}

const INITIAL_VALUES: FormValues = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  idNumber: '',
  gradeLevel: '',
  clientId: '',
  programmeId: '',
  enrollmentDate: '',
  notes: '',
};

const GRADE_LEVELS = ['Grade R', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'];

const stepDescriptions: Record<string, string> = {
  details: "Enter the student's personal information. Students are linked to clients (schools or organisations).",
  'client-programme': "Select the client this student is associated with and link to an existing tutoring agreement if applicable.",
  review: "Review all information before creating the student record.",
};

export const StudentWizard = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const evaluateRef = useRef<(() => Promise<{ allowed: boolean; reason?: string }>) | null>(null);

  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    if (evaluateRef.current) {
      const ruleResult = await evaluateRef.current();
      if (!ruleResult.allowed) {
        throw new Error(ruleResult.reason || 'Rule evaluation failed');
      }
    }

    const entityResult = await dataProvider.create('tutoring-students', {
      data: {
        firstName: v.firstName,
        lastName: v.lastName,
        dateOfBirth: v.dateOfBirth,
        idNumber: v.idNumber,
        gradeLevel: v.gradeLevel,
        clientId: v.clientId,
        programmeId: v.programmeId,
        enrollmentDate: v.enrollmentDate,
        notes: v.notes,
        status: 'Active',
      },
    });

    const entityId = entityResult.data.id;
    const { finalState, transitions } = buildTransitionChain('student', 'draft', ['ENROL']);

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'student',
        entityId,
        entityName: `${v.firstName} ${v.lastName}`,
        currentState: { value: finalState, context: v },
        status: 'active',
        transitions,
      },
    });

    return { id: entityId };
  }, [dataProvider]);

  const {
    currentStep, currentStepIndex, totalSteps, isFirstStep, isLastStep,
    isSaving, isCompleted, progress, values, errors, stepFeedback,
    nextStep, prevStep, updateValues, complete, reset,
  } = useWizard({
    steps: STEPS,
    initialValues: INITIAL_VALUES as unknown as Record<string, unknown>,
    dataProvider,
    onComplete: handleWizardComplete,
    stepValidators: {
      details: (vals) => {
        const v = vals as unknown as FormValues;
        const errs: Record<string, string> = {};
        if (!v.firstName?.trim()) errs.firstName = 'First name is required';
        return { isValid: Object.keys(errs).length === 0, errors: errs };
      },
      'client-programme': (vals) => {
        const v = vals as unknown as FormValues;
        const errs: Record<string, string> = {};
        if (!v.clientId?.trim()) errs.clientId = 'Client is required';
        return { isValid: Object.keys(errs).length === 0, errors: errs };
      },
    },
  });

  const handleNext = () => {
    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const updateField = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value });
  };

  const vals = values as unknown as FormValues;
  const { evaluate } = useRuleEvaluation({ ruleId: 'student:create', context: vals as unknown as Record<string, unknown> });
  useEffect(() => {
    evaluateRef.current = evaluate;
  }, [evaluate]);

  const renderFormFields = () => {
    switch (currentStep.id) {
      case 'details':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="First Name" value={vals.firstName} onChange={e => updateField('firstName', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Last Name" value={vals.lastName} onChange={e => updateField('lastName', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Date of Birth" type="date" value={vals.dateOfBirth} onChange={e => updateField('dateOfBirth', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="ID Number" value={vals.idNumber} onChange={e => updateField('idNumber', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Grade Level</InputLabel>
                <Select value={vals.gradeLevel} label="Grade Level" onChange={e => updateField('gradeLevel', e.target.value)}>
                  {GRADE_LEVELS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );
      case 'client-programme':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Client ID" value={vals.clientId} onChange={e => updateField('clientId', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Programme ID" value={vals.programmeId} onChange={e => updateField('programmeId', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Enrollment Date" type="date" value={vals.enrollmentDate} onChange={e => updateField('enrollmentDate', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Notes" value={vals.notes} onChange={e => updateField('notes', e.target.value)} fullWidth multiline rows={3} />
            </Grid>
          </Grid>
        );
      case 'review':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 2 }}>Review student details before creating.</Alert>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Student Info</Typography>
                <Typography variant="body2"><strong>Name:</strong> {vals.firstName} {vals.lastName}</Typography>
                <Typography variant="body2"><strong>DOB:</strong> {vals.dateOfBirth}</Typography>
                <Typography variant="body2"><strong>Grade:</strong> {vals.gradeLevel}</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Programme Info</Typography>
                <Typography variant="body2"><strong>Client:</strong> {vals.clientId}</Typography>
                <Typography variant="body2"><strong>Programme:</strong> {vals.programmeId}</Typography>
                <Typography variant="body2"><strong>Enrolled:</strong> {vals.enrollmentDate}</Typography>
              </Paper>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={prevStep}
      onSaveDraft={() => {}}
      onNext={handleNext}
      onComplete={complete}
      onReset={reset}
    >
      {isCompleted ? (
        <OutcomeSummary
          title="Student Created"
          processId={(values as { id: string }).id}
          fields={[
            { label: 'Name', value: `${vals.firstName} ${vals.lastName}` },
            { label: 'Grade', value: vals.gradeLevel },
            { label: 'Client', value: vals.clientId },
          ]}
          footerActions={[
            { label: 'View Students', to: '/tutoring-students' },
            { label: 'Create Another', onClick: reset },
          ]}
        />
      ) : (
        <WizardStep
          stepNumber={currentStepIndex + 1}
          totalSteps={totalSteps}
          title={currentStep.title}
          description={stepDescriptions[currentStep.id]}
        >
          {stepFeedback && <StepFeedback type={stepFeedback.type} messages={stepFeedback.messages} />}
          {renderFormFields()}
        </WizardStep>
      )}
    </WizardLayout>
  );
};
